import React from "react";
import type { WorkspaceTab } from "./workspace-context";
import { useWorkspace } from "./workspace-context";
import {
  History,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  RefreshCw,
  X,
  Lock,
  Unlock,
} from "lucide-react";

interface WindowFrameProps {
  tab: WorkspaceTab;
  isMinimized?: boolean;
  isMinimizing?: boolean;
  isActive?: boolean;
  onMinimize: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

export default function WindowFrame({
  tab,
  isMinimized,
  isMinimizing,
  isActive,
  onMinimize,
  onClose,
  children,
}: WindowFrameProps) {
  const {
    beginWindowDrag,
    updateWindowDrag,
    endWindowDrag,
    state,
    toggleMaximize,
    toggleTabLocked,
    isTabLocked,
  } = useWorkspace();

  const isLocked = isTabLocked(tab.id);

  const isMaximized = tab.tilePosition === "fullscreen";
  const isDraggingThis = Boolean(state.windowDragState?.isDragging && state.windowDragState.tabId === tab.id);

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const debugFrames = React.useMemo(() => {
    try {
      const qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
      const qsVal = qs?.get("debugFrames");
      return qsVal === "1" || qsVal === "true";
    } catch {
      return false;
    }
  }, []);

  React.useLayoutEffect(() => {
    if (!debugFrames || !rootRef.current) return;
    const logSize = () => {
      const r = rootRef.current!.getBoundingClientRect();
      // eslint-disable-next-line no-console
      console.debug("[WindowFrame] bounds:", { tabId: tab.id, title: tab.title, x: r.x, y: r.y, width: r.width, height: r.height });
      try {
        window.dispatchEvent(
          new CustomEvent("workspace:frame-bounds", {
            detail: { tabId: tab.id, x: r.left, y: r.top, width: r.width, height: r.height },
          }),
        );
      } catch {}
    };
    logSize();
    const ro = new ResizeObserver(() => logSize());
    ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, [tab.id, tab.title, debugFrames]);

  const transitionBase =
    "transition-all duration-200 ease-out will-change-transform will-change-opacity";
  const minimizeStyles = isMinimizing
    ? { transform: "translateY(24px) scaleY(0.95)", opacity: 0.0 }
    : undefined;
  const dragHideStyles = isDraggingThis
    ? { transform: "scale(0.92)", opacity: 0.0 }
    : undefined;

  const onHeaderMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      beginWindowDrag(tab.id, { x: e.clientX, y: e.clientY });
      const onMove = (ev: MouseEvent) => {
        updateWindowDrag({ x: ev.clientX, y: ev.clientY });
      };
      const onUp = () => {
        endWindowDrag();
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp, { once: true });
    },
    [beginWindowDrag, updateWindowDrag, endWindowDrag, tab.id],
  );

  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState(tab.title);
  const titleInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  React.useEffect(() => {
    setDraftTitle(tab.title);
  }, [tab.title]);

  const commitTitle = React.useCallback(
    (next: string) => {
      const safe = next.trim();
      setIsEditingTitle(false);
      try {
        window.dispatchEvent(
          new CustomEvent("chat:rename-thread", { detail: { tabId: tab.id, title: safe } }),
        );
      } catch {
        // ignore dispatch failures
      }
    },
    [tab.id],
  );

  return (
    <div
      ref={rootRef}
      className={`
        relative flex flex-col w-full h-full min-h-full min-w-full
        rounded-xl
        bg-[linear-gradient(180deg,rgba(28,18,60,.55),rgba(10,6,25,.85))]
        backdrop-blur-xl

        border border-purple-500/25

        ${transitionBase}
        ${isDraggingThis ? "pointer-events-none" : ""}
      `}
      style={{ ...(minimizeStyles ?? {}), ...(dragHideStyles ?? {}) }}
    >
      {debugFrames ? (
        <div className="pointer-events-none absolute inset-0 z-[70]">
          <div className="absolute inset-0 border-2 border-cyan-400/80 border-dashed" />
        </div>
      ) : null}

      {/* Top header as tall border with rounded top corners */}
      <div
        className={`
          h-8 px-3
          flex items-center justify-between
          rounded-t-xl

          bg-[linear-gradient(180deg,rgba(88,28,135,.95),rgba(67,20,120,.95))]

          border-b border-purple-400/30
          backdrop-blur-md

          ${isLocked ? "cursor-default" : "cursor-move active:cursor-grabbing"}
        `}
        onMouseDown={isLocked ? undefined : onHeaderMouseDown}
      >
        {/* Tool title (left) */}
        <div className="flex-1 flex items-center gap-2">
          <div
            className="flex items-center gap-1"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (tab.type === "chat") {
                setDraftTitle(tab.title);
                setIsEditingTitle(true);
              }
            }}
          >
            {isEditingTitle && tab.type === "chat" ? (
              <input
                ref={titleInputRef}
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                onBlur={() => commitTitle(draftTitle || tab.title)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitTitle(draftTitle || tab.title);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    setIsEditingTitle(false);
                    setDraftTitle(tab.title);
                  }
                }}
                className="text-[14px] font-semibold text-white/90 bg-transparent border border-white/30 rounded-sm px-1 py-[2px] w-[160px] outline-none"
              />
            ) : (
              <span className="text-[14px] font-semibold tracking-wide text-white/90 truncate">
                {tab.title}
              </span>
            )}
          </div>

          {tab.type === "chat" ? (
            <button
              type="button"
              aria-label="Start a new chat thread"
              title="New chat"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("chat:new-thread", { detail: { tabId: tab.id } }),
                  );
                } catch {
                  // ignore dispatch failures
                }
              }}
              className="inline-flex items-center justify-center rounded-sm border border-white/30 bg-white/10 hover:bg-white/20"
              style={{ width: 18, height: 18 }}
            >
              <Plus className="h-3 w-3 text-white" strokeWidth={2.5} />
            </button>
          ) : null}

          {tab.type === "chat" ? (
            <button
              type="button"
              aria-label="Open chat history"
              title="History"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("chat:open-history", { detail: { tabId: tab.id } }),
                  );
                } catch {
                  // ignore dispatch failures
                }
              }}
              className="inline-flex items-center justify-center rounded-sm border border-white/30 bg-white/10 hover:bg-white/20"
              style={{ width: 18, height: 18 }}
            >
              <History className="h-3 w-3 text-white" strokeWidth={2.5} />
            </button>
          ) : null}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">

        {/* Lock */}
        {/* <button
          type="button"
          aria-label={isLocked ? "Unlock window" : "Lock window"}
          title={isLocked ? "Unlock position" : "Lock position"}
          onClick={() => toggleTabLocked(tab.id)}
          onMouseDown={(e) => e.stopPropagation()}
          className={`
            relative inline-flex items-center justify-center
            rounded-md border transition-all duration-150
            ${isLocked
              ? "bg-purple-600/90 border-purple-400 text-white shadow-[0_0_12px_rgba(168,85,247,.7)] scale-105"
              : "bg-zinc-800/80 border-zinc-600 text-zinc-400 hover:bg-zinc-700"}
          `}
          style={{ width: 18, height: 18 }}
        >
          {isLocked ? (
            <Lock className="h-3 w-3" strokeWidth={2.5} />
          ) : (
            <Unlock className="h-3 w-3 opacity-60" strokeWidth={2.5} />
          )}
        </button> */}

          {/* Refresh (only for chat tabs) */}
          {tab.type === "chat" ? (
            <button
              type="button"
              aria-label="Refresh chat threads"
              title="Refresh"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("chat:refresh", { detail: { tabId: tab.id } }),
                  );
                } catch {
                  // ignore dispatch failures
                }
              }}
              className="relative inline-flex items-center justify-center rounded-full bg-purple-400/80 hover:bg-purple-300/80 text-white"
              style={{ width: 14, height: 14 }}
            >
              <RefreshCw className="h-3 w-3" strokeWidth={2.5} />
            </button>
          ) : null}
          {/* Minimize (Yellow) */}
          <button
            type="button"
            aria-label="Minimize window"
            onClick={onMinimize}
            onMouseDown={(e) => e.stopPropagation()}
            className="relative inline-flex items-center justify-center rounded-full"
            style={{ width: 14, height: 14, backgroundColor: "#eab308" }}
          >
            <Minus className="h-3 w-3 text-zinc-800/90" strokeWidth={3} />
          </button>

          {/* Maximize (Green) */}
          <button
            type="button"
            aria-label={isMaximized ? "Restore window" : "Maximize window"}
            onClick={() => toggleMaximize(tab.id)}
            onMouseDown={(e) => e.stopPropagation()}
            className="group relative inline-flex items-center justify-center rounded-full"
            style={{ width: 14, height: 14, backgroundColor: "#22c55e" }}
          >
            {isMaximized ? (
              <Minimize2 className="h-2.5 w-2.5 text-emerald-900/90" strokeWidth={2} />
            ) : (
              <Maximize2 className="h-2.5 w-2.5 text-emerald-900/90" strokeWidth={2} />
            )}
          </button>
          
          {/* Close (Red) */}
          <button
            type="button"
            aria-label={`Close ${tab.title}`}
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="relative inline-flex items-center justify-center rounded-full"
            style={{ width: 14, height: 14, backgroundColor: "#ef4444" }}
          >
            <X className="h-3 w-3 text-red-900/90" strokeWidth={2.5} />
          </button>
        </div>
      </div>
      {/* Sides and bottom border, rounded bottom corners */}
      <div className="flex-1 bg-background border border-border border-t-0 rounded-b-sm overflow-hidden min-h-0">
        <div className="w-full h-full overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
}
